import { useEffect, useState } from "react";
import { calenderClock, calenderIcon, dropDown } from "../../asset";
import { colorMap, days, periods } from "../../util/Constants";
import { AppDispatch, RootState } from "../../store";
import { useSelector } from "react-redux";
import { AddStaffForm } from "../../types/staff";
import useDropdown from "../../util/hooks/useDropDown";
import { useBranchCode } from "../../util/hooks/useBranch";
import { useDispatch } from "react-redux";
import { fetchStaff } from "../../features/staff/staffSlice";
import SelectClassModal from "../../components/modal/SelectClassModel";
import { fetchDashboardData } from "../../features/dashboard/dashboardSlice";
import SelectStaffModal from "../../components/modal/AddTeachersToGroup";
import { AccessibleClassRoom } from "../../types/schoolAccess";

const transformSchedule = (schedule: any) => {
  if (!schedule) return {};

  const dayMap: Record<string, string> = {
    MONDAY: "Mon",
    TUESDAY: "Tue",
    WEDNESDAY: "Wed",
    THURSDAY: "Thu",
    FRIDAY: "Fri",
    SATURDAY: "Sat",
    SUNDAY: "Sun",
  };

  const result: any = {};

  const colorKeys = Object.keys(colorMap);
  let globalColorIndex = 0;

  Object.keys(schedule).forEach((backendDay) => {
    const uiDay = dayMap[backendDay];
    if (!uiDay) return;

    result[uiDay] = {};

    schedule[backendDay].forEach((item: any) => {
      const periodKey = `Period ${item.periodNumber}`;

      if (!result[uiDay][periodKey]) {
        result[uiDay][periodKey] = [];
      }

      if (item.subjectName) {
        const assignedColor = colorKeys[globalColorIndex % colorKeys.length];

        globalColorIndex++;

        result[uiDay][periodKey].push({
          subject: item.subjectName,
          teacher: item.staffName,
          time: "",
          color: assignedColor,
        });
      }
    });
  });

  return result;
};

const ScheduleCard = ({ subject, time, teacher, color }) => (
  <div
    className={`border text-center rounded-lg px-2 py-1 gap-1 text-xs flex flex-col justify-center items-center w-24 h-12 ${colorMap[color]}`}
  >
    <p className="font-medium text-[13px] line-clamp-1">{subject}</p>
    <div className="flex items-center gap-1 text-[10px] text-neutral-500">
      {time !== "" && (
        <span className="flex gap-1 items-center">
          <img src={calenderClock} alt="" className="w-[10px] h-[10px]" />{" "}
          {time}
        </span>
      )}
      <span className="flex gap-1 items-center">
        <img src={calenderIcon} alt="" className="w-[9px] h-[9px]" /> {teacher}
      </span>
    </div>
  </div>
);

const WeeklySchedule = () => {
  const [activeTab, setActiveTab] = useState("Staff Schedule");
  const [openStaffModal, setOpenStaffModal] = useState(false);
  const [openClassModal, setOpenClassModal] = useState(false);
  const [selectedClass, setSelectedClass] = useState<{
    classId: number;
    className: string;
  } | null>(null);

  const { data: dashboardData } = useSelector(
    (state: RootState) => state.dashboard,
  );

  const today = new Date().toLocaleDateString("en-US", {
    weekday: "short",
  });
  const dispatch = useDispatch<AppDispatch>();
  const { staffList } = useSelector((state: RootState) => state.staff);

  const [selectedStaff, setSelectedStaff] = useState<AddStaffForm | null>(null);
  const { isOpen, toggleDropdown, closeDropdown, dropdownRef } = useDropdown();

  const rawSchedule =
    activeTab === "Staff Schedule"
      ? dashboardData?.staffSchedule
      : dashboardData?.classSchedule;

  const data = transformSchedule(rawSchedule);

  const hasData = rawSchedule && Object.keys(rawSchedule).length > 0;

  const branchCode = useBranchCode();

  useEffect(() => {
    if (!branchCode) return;

    dispatch(
      fetchStaff({
        branchCode,
        page: 0,
        size: 100,
      }),
    );
  }, [dispatch, branchCode]);

  const handleClassSelect = (selectedClasses: AccessibleClassRoom[]) => {
    const selected = selectedClasses[0];

    if (!selected) return;

    setSelectedClass({
      classId: selected.classId,
      className: selected.className,
    });

    setOpenClassModal(false);

    if (branchCode) {
      dispatch(
        fetchDashboardData({
          branchCode,
          classId: selected.classId,
        }),
      );
    }
  };

  return (
    <div className="bg-white rounded-2xl pb-6 shadow-[0_0_54px_0_rgba(0,0,0,0.05)] min-h-[400px]">
      {/* Header */}
      <div className="flex justify-between items-center p-6">
        <p className="font-semibold text-base text-neutral-500 leading-5">
          Weekly Schedule
        </p>
        {activeTab === "Staff Schedule" && (
          <div
            ref={dropdownRef}
            //  onClick={() => setOpenStaffModal(true)}
            className="flex items-center gap-[6px] text-neutral-500 cursor-pointer relative"
          >
            <div
              className="flex items-center gap-[6px]"
              onClick={toggleDropdown}
            >
              <span className="w-[6px] h-[6px] rounded-full bg-neutral-500" />

              <p>
                Staff :{" "}
                <span className="text-success-600 font-semibold">
                  {selectedStaff?.fullName || "Select Staff"}
                </span>
              </p>

              <img src={dropDown} alt="drop-down" />
            </div>

            {/* Dropdown List */}
            {isOpen && (
              <div className="absolute top-7 right-0 bg-white shadow-lg rounded-md w-52 max-h-60 overflow-y-auto z-50 border">
                {staffList?.map((staff) => (
                  <div
                    key={staff.staffId}
                    className="px-4 py-2 hover:bg-background-200 cursor-pointer text-sm"
                    onClick={() => {
                      setSelectedStaff(staff);
                      closeDropdown();
                    }}
                  >
                    {staff.fullName}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === "Class Schedule" && (
          <div
            className="flex items-center gap-[6px] text-neutral-500 cursor-pointer"
            onClick={() => setOpenClassModal(true)}
          >
            <span className="w-[6px] h-[6px] rounded-full bg-neutral-500" />

            <p>
              Class :{" "}
              <span className="text-success-600 font-semibold">
                {selectedClass?.className || "Select Class"}
              </span>
            </p>

            <img src={dropDown} alt="drop-down" />
          </div>
        )}
      </div>

      {/* Tabs */}
      <div className="flex items-center w-full justify-evenly text-xs font-semibold">
        {["Staff Schedule", "Class Schedule"].map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`w-full py-[13px] transition-colors ${
              activeTab === tab
                ? "bg-success-600 text-white"
                : "text-neutral-300"
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* if no data */}
      {!hasData ? (
        <div className="flex flex-col justify-center gap-5 items-center mt-16">
          <p className="text-[#7C7C7C] text-2xl font-medium">
            {activeTab === "Staff Schedule"
              ? "View Your Staff’s Weekly Schedule"
              : "View Your Class’s Weekly Schedule"}
          </p>
          <div
            className="flex gap-2 cursor-pointer"
            onClick={() => {
              if (activeTab === "Staff Schedule") {
                setOpenStaffModal(true);
              } else {
                setOpenClassModal(true);
              }
            }}
          >
            <p className="text-[13px] leading-4 text-neutral-500">
              {activeTab === "Staff Schedule"
                ? "Select staff to proceed"
                : "Select class to proceed"}
            </p>
            <img src={dropDown} alt="drop-down" />
          </div>
        </div>
      ) : (
        <>
          {/* Table Header */}
          <div className="grid grid-cols-[80px_repeat(6,1fr)] border-t pl-5 pr-2">
            <div className="py-3 text-[13px] text-neutral-300 font-medium">
              Day
            </div>
            {days.map((day) => (
              <div
                key={day}
                className={`py-3 text-[13px] text-neutral-300 text-center ${
                  day === today ? "font-semibold" : "font-medium"
                }`}
              >
                {day}
              </div>
            ))}
          </div>

          {/* Period Rows */}
          {periods.map((period) => (
            <div
              key={period}
              className="grid grid-cols-[80px_repeat(6,1fr)] relative items-center pl-5 pr-2"
            >
              <div className="py-3 text-sm font-medium text-neutral-300 z-10">
                {period}
              </div>

              {/* Cards  */}
              {days.map((day, index) => (
                <div
                  key={day}
                  className="p-2 flex flex-col gap-2 relative z-10"
                >
                  {data?.[day]?.[period]?.map((item, idx) => (
                    <ScheduleCard key={idx} {...item} />
                  ))}
                </div>
              ))}

              <div className="absolute top-8 left-[90px] right-4 h-px bg-neutral-50 z-0" />
            </div>
          ))}
        </>
      )}
      {openStaffModal && (
        <SelectStaffModal
          title="Select Staff"
          actionLabel="View Schedule"
          multiple={false}
          onClose={() => setOpenStaffModal(false)}
          onAction={(selected) => {
            const staff = selected[0];
            setSelectedStaff(staff);

            if (branchCode) {
              dispatch(
                fetchDashboardData({
                  branchCode,
                  staffId: staff.staffId,
                }),
              );
            }
          }}
        />
      )}

      {openClassModal && (
        <SelectClassModal
          selectionMode="single"
          title="Select Class"
          actionLabel="View Schedule"
          onClose={() => setOpenClassModal(false)}
          onAction={handleClassSelect}
        />
      )}
    </div>
  );
};

export default WeeklySchedule;
